import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Delete as DeleteIcon, VpnKey as VpnKeyIcon } from "@mui/icons-material";
import { useUser } from "./UserContext";
import { listUsers, deleteUser, setUserPassword } from "../api/agentApi";

// Password validation constants
const specialChars = "!@#$%^&*";
const passwordHint =
  `Password must be at least 8 characters and contain at least 1 uppercase letter, 1 numeral, and 1 special character (${specialChars}).`;
function validatePassword(password: string): boolean {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[!@#$%^&*]/.test(password)
  );
}

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  created_at: string;
  password_set_by_operator: boolean;
}

interface DeleteDialogState {
  open: boolean;
  user: User | null;
}

interface ResetPasswordDialogState {
  open: boolean;
  user: User | null;
  newPassword: string;
  confirmPassword: string;
  passwordValid: boolean;
  passwordsMatch: boolean;
}

const UserListPage: React.FC = () => {
  const { user: currentUser } = useUser();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [offset, setOffset] = useState(0);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);

  // Delete dialog state
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    user: null,
  });

  // Reset password dialog state
  const [resetPasswordDialog, setResetPasswordDialog] = useState<ResetPasswordDialogState>({
    open: false,
    user: null,
    newPassword: "",
    confirmPassword: "",
    passwordValid: true,
    passwordsMatch: true,
  });

  // Fetch users
  useEffect(() => {
    if (!(currentUser?.role === "operator-admin" || currentUser?.role === "admin")) return;

    const fetchUsers = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await listUsers(offset, limit);
        setUsers(response.users || []);
        setTotal(response.total || 0);
      } catch (err: any) {
        setError(err?.message || "Failed to load users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [currentUser, offset, limit]);

  // Access control
  if (!currentUser || !(currentUser.role === "operator-admin" || currentUser.role === "admin")) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Only operator and admin users can list users.
        </Alert>
      </Box>
    );
  }

  // Handle delete user
  const handleDeleteClick = (user: User) => {
    setDeleteDialog({ open: true, user });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.user) return;

    try {
      await deleteUser(deleteDialog.user.id);
      setSuccessMessage(`User "${deleteDialog.user.username}" deleted successfully`);
      setDeleteDialog({ open: false, user: null });

      // Refresh user list
      const response = await listUsers(offset, limit);
      setUsers(response.users || []);
      setTotal(response.total || 0);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      // Handle both string messages and API error objects
      let errorMessage = "Failed to delete user";

      const rawMessage =
        err && typeof err.message === "string" ? err.message : undefined;

      if (rawMessage) {
        try {
          // Try to parse JSON error response thrown by apiCall (res.text())
          const parsed = JSON.parse(rawMessage);
          const detail = (parsed as any).detail ?? (parsed as any).message;

          if (typeof detail === "string") {
            errorMessage = detail;
          } else if (Array.isArray(detail)) {
            errorMessage = detail.join(", ");
          } else if (detail && typeof detail === "object") {
            errorMessage =
              typeof (detail as any).message === "string"
                ? (detail as any).message
                : JSON.stringify(detail);
          } else {
            errorMessage = rawMessage;
          }
        } catch {
          // Not JSON: use the raw message text
          errorMessage = rawMessage;
        }
      } else if (err && typeof err.detail === "string") {
        errorMessage = err.detail;
      } else if (typeof err === "string") {
        errorMessage = err;
      }
      setError(errorMessage);
      // Close dialog; error message is displayed separately for the user to see
      setDeleteDialog({ open: false, user: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, user: null });
  };

  // Handle reset password
  const handleResetPasswordClick = (user: User) => {
    setResetPasswordDialog({
      open: true,
      user,
      newPassword: "",
      confirmPassword: "",
      passwordValid: true,
      passwordsMatch: true,
    });
  };

  const handlePasswordChange = (password: string) => {
    setResetPasswordDialog((prev) => ({
      ...prev,
      newPassword: password,
      passwordValid: validatePassword(password),
      passwordsMatch: password === prev.confirmPassword,
    }));
  };

  const handleConfirmPasswordChange = (password: string) => {
    setResetPasswordDialog((prev) => ({
      ...prev,
      confirmPassword: password,
      passwordsMatch: prev.newPassword === password,
    }));
  };

  const handleResetPasswordConfirm = async () => {
    if (!resetPasswordDialog.user) return;

    if (!resetPasswordDialog.passwordsMatch) {
      setError("Passwords must match");
      return;
    }

    if (!validatePassword(resetPasswordDialog.newPassword)) {
      setError("Password does not meet requirements");
      return;
    }

    try {
      await setUserPassword(resetPasswordDialog.user.id, resetPasswordDialog.newPassword);
      setSuccessMessage(
        `Password reset successfully for user "${resetPasswordDialog.user.username}"`
      );
      setResetPasswordDialog({
        open: false,
        user: null,
        newPassword: "",
        confirmPassword: "",
        passwordValid: true,
        passwordsMatch: true,
      });

      // Refresh user list to update password_set_by_operator status
      const response = await listUsers(offset, limit);
      setUsers(response.users || []);
      setTotal(response.total || 0);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err: any) {
      setError(err?.message || "Failed to reset password");
      setResetPasswordDialog({
        open: false,
        user: null,
        newPassword: "",
        confirmPassword: "",
        passwordValid: true,
        passwordsMatch: true,
      });
    }
  };

  const handleResetPasswordCancel = () => {
    setResetPasswordDialog({
      open: false,
      user: null,
      newPassword: "",
      confirmPassword: "",
      passwordValid: true,
      passwordsMatch: true,
    });
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (offset > 0) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  const handleNextPage = () => {
    if (offset + limit < total) {
      setOffset(offset + limit);
    }
  };

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit) || 1;

  return (
    <Box sx={{ pt: 1, px: 3 }}>
      <Toolbar disableGutters sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h4">User Management</Typography>
        <Button
          component={Link}
          to="/add-user"
          variant="contained"
          color="primary"
          sx={{ fontSize: "1rem", px: 3, py: 1 }}
        >
          Add User
        </Button>
      </Toolbar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
          {successMessage}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Username</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: "1rem" }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No users found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover sx={{ fontSize: "0.95rem" }}>
                    <TableCell sx={{ py: 1, fontSize: "0.95rem" }}>
                      {user.username}
                    </TableCell>
                    <TableCell sx={{ py: 1, fontSize: "0.95rem" }}>{user.email}</TableCell>
                    <TableCell sx={{ py: 1, fontSize: "0.95rem" }}>
                      <Chip
                        label={user.role}
                        size="small"
                        sx={{
                          bgcolor:
                            user.role === "operator-admin"
                              ? "#f44336"
                              : user.role === "admin"
                              ? "#ff9800"
                              : "#2196f3",
                          color: "#fff",
                          fontWeight: 500,
                          borderRadius: "9999px",
                          height: 24,
                          px: 1.5,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1, fontSize: "0.95rem" }}>
                      <Tooltip title={user.created_at}>
                        <span>{new Date(user.created_at).toLocaleDateString()}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ py: 1, fontSize: "0.95rem" }}>
                      {user.password_set_by_operator && (
                        <Chip
                          label="Password Reset Required"
                          size="small"
                          sx={{
                            bgcolor: "#ff9800",
                            color: "#fff",
                            fontWeight: 500,
                            borderRadius: "9999px",
                            height: 24,
                            px: 1.5,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1 }} align="center">
                      <Tooltip title="Reset Password">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleResetPasswordClick(user)}
                        >
                          <VpnKeyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {currentUser && currentUser.role === "operator-admin" && (
                        <Tooltip title="Delete User">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(user)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Button
          disabled={offset === 0}
          onClick={handlePrevPage}
          variant="outlined"
        >
          Prev
        </Button>
        <Typography variant="body2" sx={{ mx: 2 }}>
          Page {currentPage} of {totalPages}
        </Typography>
        <Button
          disabled={offset + limit >= total}
          onClick={handleNextPage}
          variant="outlined"
        >
          Next
        </Button>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete user{" "}
            <strong>{deleteDialog.user?.username}</strong>? This action cannot be undone.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: "warning.main", fontWeight: 500 }}>
            Note: Users with associated keys cannot be deleted until the keys are deleted first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={resetPasswordDialog.open}
        onClose={handleResetPasswordCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Reset password for user{" "}
            <strong>{resetPasswordDialog.user?.username}</strong>
          </DialogContentText>
          <TextField
            label="New Password"
            type="password"
            fullWidth
            value={resetPasswordDialog.newPassword}
            onChange={(e) => handlePasswordChange(e.target.value)}
            error={
              !resetPasswordDialog.passwordValid &&
              resetPasswordDialog.newPassword.length > 0
            }
            helperText={passwordHint}
            margin="normal"
          />
          <TextField
            label="Confirm Password"
            type="password"
            fullWidth
            value={resetPasswordDialog.confirmPassword}
            onChange={(e) => handleConfirmPasswordChange(e.target.value)}
            error={
              !resetPasswordDialog.passwordsMatch &&
              resetPasswordDialog.confirmPassword.length > 0
            }
            helperText={
              !resetPasswordDialog.passwordsMatch &&
              resetPasswordDialog.confirmPassword.length > 0
                ? "Passwords must match"
                : ""
            }
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetPasswordCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleResetPasswordConfirm}
            color="primary"
            variant="contained"
            disabled={
              !resetPasswordDialog.newPassword ||
              !resetPasswordDialog.confirmPassword ||
              !resetPasswordDialog.passwordValid ||
              !resetPasswordDialog.passwordsMatch
            }
          >
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserListPage;
